import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs';
import { cloneDeep, mergeWith } from 'lodash-es';
import { MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { CreateModuleRightsDirective } from '../../../core/auth/directives/can-create-module.directive';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption,
  MatOptgroup,
} from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { UtilityService } from '../../services/utility.service';
import type { Entry, Module } from '../../../features/admin/modules/modules.model';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { FormlyErrorComponent } from '../formly-error.component';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { FormlyFieldAutocomplete, AUTOCOMPLETE_TEMPLATE } from './autocomplete.component';
import { entryLoadValues } from './entry-loader.util';
import { markUserEdit } from './user-edit.util';

/**
 * "Entry inladen" — search an existing entry and copy its values into the form you are filling in.
 *
 * Looks and searches exactly like an autocomplete, which is why it extends it: the query, the
 * paging, the grouping and the dynamic filters are the same problem and stay in one place. Only the
 * ending differs. An autocomplete RELATES: it writes the picked entry as this field's value and the
 * link survives the save. This field COPIES: it writes the picked entry's values into the other
 * fields and keeps no value of its own, so the new entry starts life as a sibling of the source
 * rather than a reference to it.
 *
 * "Base this order on an existing one" is the case it was built for (design §5,
 * docs/superpowers/specs/2026-09-11-opdracht-aanmaakpagina-design.md), but nothing here knows about
 * orders: the module to search and the paths to skip are configuration.
 *
 * Configured with the same options block as an autocomplete, plus:
 * - `excludePaths` — dotted paths in the SOURCE entry that must not be copied. Identity fields are
 *   always skipped, see entry-loader.util.ts.
 */
@Component({
  selector: 'formly-field-entry-loader',
  template: AUTOCOMPLETE_TEMPLATE,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIcon,
    MatIconButton,
    MatTooltip,
    CreateModuleRightsDirective,
    MatHint,
    MatAutocomplete,
    MatOption,
    MatOptgroup,
    MatSuffix,
    MatInput,
    MatLabel,
    MatFormField,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    FormlyErrorComponent,
    MatProgressSpinner,
    FormlyDataCyDirective,
    FuseAlertComponent,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
  ],
  styles: `
    ::ng-deep .mdc-list-item__primary-text {
      width: 100%;
      display: inline-block;
    }

    ::ng-deep .cdk-overlay-pane {
      min-width: 320px;
    }

    ::ng-deep .mat-mdc-optgroup-label .mdc-list-item__primary-text {
      font-weight: 500;
      span {
        font-size: 14px;
        font-weight: 400 !important;
      }
    }
  `,
})
export class FormlyFieldEntryLoader extends FormlyFieldAutocomplete {
  readonly #entriesService = inject(EntriesService);
  readonly #utility = inject(UtilityService);

  /**
   * No "+". The point of this field is to start from something that already exists; a create button
   * would open the very form the user is already standing in.
   */
  override showCreateButton = computed(() => false);

  /**
   * Load instead of link. The picked label goes into the search box so the user can see what they
   * chose, but `field.formControl` is deliberately left untouched — a value here would be saved as
   * a relation to the source entry, which is the one thing this field must not create.
   */
  override onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const matchingOption = this.filteredOptions().find(option => option.label === event.option.value);
    if (!matchingOption) {
      return;
    }

    this.searchField.patchValue(matchingOption.label, { emitEvent: false });

    // The picked entry says which module it came from, and this field is configured with more than
    // one: an order can be based on a live order OR a recurring one, and the dropdown groups both.
    // Taking linkedData()[0] would fetch a recurring order's id from the live-orders module, which
    // 404s and reports "kon niet worden geladen" for an order the planner can plainly see.
    const picked = matchingOption.value as { id?: string; moduleId?: string } | undefined;
    const sourceModule = (this.linkedData().find(
      (data: { module?: Module }) => data.module?.id === picked?.moduleId
    )?.module ?? this.linkedData()[0]?.module) as Module | undefined;
    const sourceId = picked?.id;
    if (!sourceModule?.id || !sourceId) {
      // A manually configured option list has no entry behind it, so there is nothing to load.
      return;
    }

    // The dropdown option carries only what the options query selected — enough to render a line,
    // not the nested route with its stops and volumes. Fetch the entry itself before copying.
    this.isLoading.set(true);
    this.#entriesService
      .getEntry(sourceModule.id, sourceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: entry => {
          this.isLoading.set(false);
          this.#applyLoadedEntry(entry, sourceModule);
        },
        error: () => {
          this.isLoading.set(false);
          this.#utility.showMessage('Deze opdracht kon niet worden geladen.');
        },
      });
  }

  #applyLoadedEntry(entry: Entry, sourceModule: Module): void {
    const formModule = this.options.formState?.entity as Module | undefined;
    const excludePaths = (this.field.props?.['excludePaths'] as string[] | undefined) ?? [];
    // The source module says which of its keys are grouping levels, so the copied values land on
    // the flat paths this form has without anyone hardcoding a tenant's tab names.
    const values = entryLoadValues(entry, formModule, excludePaths, sourceModule);
    if (!values || !Object.keys(values).length) {
      this.#utility.showMessage('Deze opdracht bevat geen gegevens om over te nemen.');
      return;
    }

    // The ROOT group, not this field's own: a loaded entry fills the whole form, and this field can
    // sit inside a section, which would otherwise scope the patch to that section's fields.
    const root = this.form.root as FormGroup;

    // Through the MODEL, not patchValue on the built form.
    //
    // `FormArray.patchValue` only fills controls that already exist and drops the rest silently. A
    // copied order carries its crew as `users[0].users[0].user`, while this form built its rows from
    // the module defaults — so every flat field landed and the assigned driver did not.
    //
    // DetailsService merges preFilledFields into the model (details.service.ts ~384) and Formly
    // builds the repeat rows around the result. That is the route the planning boards' prefill
    // takes, and the reason a driver clicked in the agenda DOES arrive.
    //
    // Reached through formState rather than by injecting DetailsService: this field lives in the
    // shared formly library, and importing the details tree would tie every form using it to that
    // module.
    const prefillChannel = this.options.formState?.['detailsService'] as
      | { addPrefilledFields$?: { next: (value: Entry['entry']) => void } }
      | undefined;

    if (prefillChannel?.addPrefilledFields$) {
      prefillChannel.addPrefilledFields$.next(values);
      // After the model has recomputed: rebuild the structure around it, then mark dirty. Marking
      // the controls the rebuild replaces would be undone, and a pristine form leaves the save
      // button disabled and UnSavedChangesGuard asleep.
      setTimeout(() => {
        // DetailsService keeps its model while the form holds edits (#hasEditsToSubmit), so after
        // the planner touched anything — picking "Herhalend" is enough — the prefill above never
        // reached the model and the form stayed empty. Loading is an explicit request to take these
        // values, so they go into the live model as well; arrays whole, like the service merges them.
        mergeWith(this.#rootModel(), values, (_dest: unknown, src: unknown) =>
          Array.isArray(src) ? cloneDeep(src) : undefined
        );
        this.options?.build?.();
        markUserEdit(this.form.root as FormGroup);
      });
    } else {
      // No DetailsService behind this form: land the flat fields rather than nothing.
      markUserEdit(root);
      root.patchValue(values);
    }

    this.#utility.showMessage('Gegevens overgenomen. Controleer ze voordat je opslaat.');
  }

  /** The model of the whole form; this field's own `model` is scoped to its parent. */
  #rootModel(): Record<string, unknown> {
    let root = this.field;
    while (root.parent) root = root.parent;
    return root.model as Record<string, unknown>;
  }
}
